import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Locale = "zh-CN" | "en";
const messages = {
  "zh-CN": {
    overview: "安全总览",
    organizations: "组织",
    users: "用户",
    sites: "网站",
    events: "验证事件",
    risk: "风险决策",
    rules: "风险规则",
    ai: "AI 分析",
    settings: "系统设置",
    billing: "套餐与账单",
    operations: "运行状态",
    audit: "审计日志",
    signOut: "退出登录",
    verifications: "验证次数",
    passRate: "通过率",
    challengeRate: "挑战率",
    highRisk: "高风险",
    recent: "最近风险决策",
    health: "系统健康",
    noData: "暂无真实数据",
    investigate: "调查",
    create: "创建",
    cancel: "取消",
    save: "保存",
    name: "名称",
    status: "状态",
    action: "操作",
    site: "网站",
    score: "评分",
    decision: "决策",
    time: "时间",
    loading: "正在加载",
    retry: "重试",
    closeNavigation: "关闭导航",
    openNavigation: "打开导航",
    primaryNavigation: "主导航",
    console: "控制台",
    language: "语言",
    changeLanguage: "切换语言",
    theme: "主题",
    changeTheme: "切换主题",
  },
  en: {
    overview: "Security overview",
    organizations: "Organizations",
    users: "Users",
    sites: "Sites",
    events: "Verification events",
    risk: "Risk decisions",
    rules: "Risk rules",
    ai: "AI analysis",
    settings: "System settings",
    billing: "Plans & billing",
    operations: "Operations",
    audit: "Audit log",
    signOut: "Sign out",
    verifications: "Verifications",
    passRate: "Pass rate",
    challengeRate: "Challenge rate",
    highRisk: "High risk",
    recent: "Recent risk decisions",
    health: "System health",
    noData: "No live data yet",
    investigate: "Investigate",
    create: "Create",
    cancel: "Cancel",
    save: "Save",
    name: "Name",
    status: "Status",
    action: "Action",
    site: "Site",
    score: "Score",
    decision: "Decision",
    time: "Time",
    loading: "Loading",
    retry: "Retry",
    closeNavigation: "Close navigation",
    openNavigation: "Open navigation",
    primaryNavigation: "Primary navigation",
    console: "Console",
    language: "Language",
    changeLanguage: "Change language",
    theme: "Theme",
    changeTheme: "Change theme",
  },
} as const;

export type MessageKey = keyof (typeof messages)["en"];

export function translate(locale: Locale, key: MessageKey): string {
  return messages[locale][key];
}
const LocaleContext = createContext<{
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
} | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    localStorage.getItem("lv_locale") === "en" ? "en" : "zh-CN",
  );
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo(
    () => ({
      locale,
      setLocale(next: Locale) {
        localStorage.setItem("lv_locale", next);
        setLocaleState(next);
      },
      t(key: MessageKey) {
        return translate(locale, key);
      },
    }),
    [locale],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("LocaleProvider is missing");
  return context;
}
